import React, { useMemo, useEffect} from 'react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import { useEditor, EditorContent } from '@tiptap/react';
import CharacterCount from '@tiptap/extension-character-count';
import HardBreak from '@tiptap/extension-hard-break';
import Chip from '@mui/material/Chip';
import clsx from 'clsx';

import { formatVariableName } from 'src/common/playbook-variables/playbook-variable-utils';
import { getSuggestion } from 'src/pages/landlord/playbooks/components/form/playbook-template-message/suggestion';
import { parseText } from 'src/pages/landlord/playbooks/components/form/playbook-template-message/PlaybookTemplateMessageV2';
import {Flex, FlexColumn} from 'src/components/flex';
import PlaybookWarning from 'src/pages/landlord/playbooks/components/PlaybookWarning';
import { PlaybookVariable } from 'src/services/api';

import styles from 'src/pages/landlord/snippets/components/form/snippet-template-message/SnippetTemplateMessage.module.css';

type Props = {
    value: string;
    onChange: (value: string) => void;
    error?: boolean;
    'data-testid'?: string;
}

export default function SnippetTemplateMessage({
    value,
    onChange,
    error,
    'data-testid': dataTestId,
}: Props) {
    const snippetVariables = Object.values(PlaybookVariable);

    const extensions = useMemo(() => [
        StarterKit,
        Mention.configure({
            HTMLAttributes: {
                class: 'mention',
            },
            suggestion: getSuggestion(snippetVariables),
            renderText: ({ node }) => `{${formatVariableName(node.attrs.id)}}`,
            renderHTML: ({ node }) => ['span', { class: 'mention' }, `{${formatVariableName(node.attrs.id)}}`]
        })
    ], [snippetVariables]);

    const editor = useEditor({
        editorProps: dataTestId ? { attributes: { 'data-testid': dataTestId } } : undefined,
        extensions: [
            CharacterCount.configure({
                limit: 2000,
            }),
            HardBreak.extend({
                addKeyboardShortcuts () {
                    return {
                        Enter: () => this.editor.commands.setHardBreak()
                    };
                }
            }),
            ...extensions,
        ],
        onUpdate: (updatedProps) => {
            onChange(updatedProps.editor.getText());
        },
    }, []);

    useEffect(() => {
        if (!editor || !snippetVariables) {
            return;
        }

        const parsed = parseText(value || '');
        const { from, to } = editor.state.selection;
        editor.commands.setContent(parsed);
        editor.commands.setTextSelection({ from, to });
    }, [editor, value]);

    const handleAddVariable = (variable: string) => () => {
        if (!editor) {
            return;
        }
        if (!variable) {
            return;
        }

        editor.commands.insertContentAt(
            editor.state.selection.anchor,
            { type: 'mention', attrs: { id: variable } }
        );
    };

    return (
        <FlexColumn>
            <div className={clsx(styles.root, error && styles.rootError)}>
                <div className={styles.border} />
                <div>
                    <EditorContent editor={editor} />
                    <Flex className={styles.toolbar} flexWrap="wrap">
                        {snippetVariables.map(variable => (
                            <Chip
                                key={variable}
                                label={`{${formatVariableName(variable)}}`}
                                onClick={handleAddVariable(variable)}
                                size="small"
                            />
                        ))}
                    </Flex>
                </div>
            </div>
            <PlaybookWarning message={value} />
        </FlexColumn>
    );
}
